import type { BaseNotification, BehaviorActionItem } from '@/@types/notification'
import { formatAsRelativeTime } from '@/utils/day'
import useNotificationClickHandler from '@/utils/hooks/useNotificationClickHandler'
import { AvatarGroup } from '@mantine/core'
import clsx from 'clsx'
import type { HTMLAttributes } from 'react'
import Avatar from '../Avatar'
import ScreenNames from '../ScreenNames'
import styles from './BehaviorNotification.module.css'
import NotificationContainer from './NotificationContainer'
import NotificationReference from './NotificationReference'

interface BehaviorNotificationData extends BaseNotification {
	actionItem: BehaviorActionItem
}

export interface BehaviorNotificationProps extends HTMLAttributes<HTMLDivElement> {
	data: BehaviorNotificationData
}

export default function BehaviorNotification({
	data,
	className,
	...props
}: BehaviorNotificationProps) {
	const {
		actionItem: { users, usersCount, behavior, iconUrl },
		referenceItem,
		createdAt,
	} = data
	const { handleClick, isClickable } = useNotificationClickHandler(data)

	return (
		<NotificationContainer
			onClick={handleClick}
			isClickable={isClickable}
			className={clsx(styles.container, className)}
			{...props}
		>
			<div className={styles.content}>
				<header className={styles.header}>
					<Avatar data={users[0]} className={styles.avatar} />
					<div className={styles.headerText}>
						<ScreenNames data={users} className={styles.screenNames} />
						<span className={styles.secondaryText}>
							{usersCount > 3 && <span>等{usersCount}人</span>}
							{behavior}
						</span>
					</div>
				</header>

				<main>
					<AvatarGroup spacing="md">
						{users.map((user) => (
							<Avatar key={user.id} data={user} className={styles.avatarGroup} />
						))}
					</AvatarGroup>
				</main>
				<footer className={styles.footer}>{formatAsRelativeTime(createdAt)}</footer>
			</div>

			<NotificationReference data={referenceItem} />
		</NotificationContainer>
	)
}
